/**
 * Global active-dealer-group context — the one place every group-scoped page
 * reads "which dealer group am I working in?" from.
 *
 * Two personas (see .claude/plans/dealer-group-switcher.md):
 *  - Operator (manage_all_dealer_groups): may select any group or "All Groups".
 *    The choice is persisted to localStorage and mirrored into the URL
 *    (?group=) so links are shareable.
 *  - Dealership user: locked to their own dealer_group_id. The switcher shows a
 *    read-only label; `dealerGroupId` always derives from their own user, never
 *    from client state — the server enforces this too, this is defense in depth.
 *
 * This is NOT the security boundary. It only expresses the operator's request;
 * the backend independently scopes every response (see dealer_group_scope.py).
 *
 * Must be mounted inside <BrowserRouter> (uses useSearchParams) and inside
 * <AuthProvider> (uses useAuth).
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from "react"
import { useLocation, useSearchParams } from "react-router-dom"

import { useAuth } from "./AuthContext"
import { DealerGroupOption, fetchDealerGroups } from "../api/dealerGroups"

// "all" is the operator-only all-groups aggregate; a number is a specific group.
export type ActiveGroup = number | "all"

const ALL_GROUPS: ActiveGroup = "all"
const STORAGE_KEY = "invi_active_dealer_group"
const URL_PARAM = "group"

interface DealerGroupContextValue {
  /** The operator's current selection. Always "all" or a group's own id for an
   *  operator; mirrors the dealership user's own group otherwise. */
  activeGroupId: ActiveGroup
  /** The value to pass to group-scoped APIs: null means "all groups". For a
   *  non-operator this is always their own group regardless of activeGroupId. */
  dealerGroupId: number | null
  /** Display name for the switcher / headers. */
  activeGroupName: string
  /** The active group's website by name ("dolanautogroup.com"), known before
   *  any page loads its data: null for All Groups or a group with no site;
   *  undefined only while an operator's group list is still loading and the
   *  answer is not yet known — a page then says nothing rather than "the
   *  website" for a moment. */
  websiteDomain: string | null | undefined
  /** Whether this caller may switch groups at all. */
  isOperator: boolean
  /** Selectable groups (operator only; empty for dealership users). */
  groups: DealerGroupOption[]
  /** Change the active group. No-op for non-operators. */
  setActiveGroup: (next: ActiveGroup) => void
}

const DealerGroupContext = createContext<DealerGroupContextValue | undefined>(undefined)

/** Parse a raw "all" | "<id>" string into an ActiveGroup, or null if unusable. */
function parseStoredGroup(raw: string | null): ActiveGroup | null {
  if (raw === ALL_GROUPS) return ALL_GROUPS
  if (raw === null || raw === "") return null
  const id = Number(raw)
  return Number.isInteger(id) && id > 0 ? id : null
}

/** Routes in the sign-in flow. The group param is for sharing a working
 *  surface; writing it here only races the redirect that leaves these pages. */
const AUTH_PATHS = new Set([
  "/login",
  "/change-password",
  "/forgot-password",
  "/reset-password",
])

export const DealerGroupProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, hasPermission, isLoading } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()

  const isOperator = hasPermission("manage_all_dealer_groups")

  // Seed from the URL first (shareable links win), then localStorage, then All.
  // Dealership users are reconciled to their own group by the effect below.
  const [activeGroupId, setActiveGroupIdState] = useState<ActiveGroup>(
    () =>
      parseStoredGroup(searchParams.get(URL_PARAM)) ??
      parseStoredGroup(localStorage.getItem(STORAGE_KEY)) ??
      ALL_GROUPS,
  )

  const [groups, setGroups] = useState<DealerGroupOption[]>([])
  const [groupsLoaded, setGroupsLoaded] = useState(false)

  const setActiveGroup = useCallback(
    (next: ActiveGroup) => {
      // Only operators may switch; dealership users are pinned server-side and
      // here too, so a stray call is a no-op rather than a silent scope change.
      if (!isOperator) return
      setActiveGroupIdState(next)
      localStorage.setItem(STORAGE_KEY, String(next))
    },
    [isOperator],
  )

  // Load the selectable groups once, for operators only. Dealership users read
  // their group name off the user object and never hit the (permission-gated)
  // /dealer-groups endpoint.
  useEffect(() => {
    if (!isOperator) return
    let cancelled = false
    ;(async () => {
      try {
        const data = await fetchDealerGroups()
        if (!cancelled) setGroups(data)
      } catch {
        // Switcher degrades to "All Groups" only; not worth surfacing.
      } finally {
        if (!cancelled) setGroupsLoaded(true)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [isOperator])

  // Reconcile the active group once auth has loaded: a dealership user is forced
  // to their own group regardless of any persisted/URL value they may carry.
  useEffect(() => {
    if (isLoading || isOperator) return
    const ownGroupId = user?.dealer_group?.id
    if (ownGroupId != null) setActiveGroupIdState(ownGroupId)
  }, [isLoading, isOperator, user])

  // Stale-group fallback: if the persisted group no longer exists in the live
  // list, drop back to All Groups rather than querying a dead id.
  useEffect(() => {
    if (!isOperator || groups.length === 0) return
    if (activeGroupId !== ALL_GROUPS && !groups.some((g) => g.id === activeGroupId)) {
      setActiveGroup(ALL_GROUPS)
    }
  }, [isOperator, groups, activeGroupId, setActiveGroup])

  // Keep the URL ?group= in sync so every link carries the active group.
  // Operators only — dealership users don't pollute the URL with a fixed group.
  //
  // NOT on the auth routes. Signing in sets the user, which makes isOperator
  // true, which fired this effect in the same tick as LoginPage's redirect —
  // and `setSearchParams(..., {replace: true})` closed over the LOGIN location,
  // so it replaced the redirect and left the operator sitting on
  // `/login?group=all`. A second click then worked, because the param was
  // already present and the guard below short-circuited. A shareable group
  // param has no meaning on a login screen anyway; it gets written as soon as
  // the operator lands on a real page.
  useEffect(() => {
    if (!isOperator) return
    if (AUTH_PATHS.has(location.pathname)) return
    if (searchParams.get(URL_PARAM) === String(activeGroupId)) return
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.set(URL_PARAM, String(activeGroupId))
        return next
      },
      { replace: true },
    )
  }, [isOperator, activeGroupId, searchParams, setSearchParams, location.pathname])

  // For a non-operator, the API scope is ALWAYS their own group — never derived
  // from switchable state. "all" maps to null (no filter).
  const dealerGroupId: number | null = useMemo(() => {
    if (!isOperator) return user?.dealer_group?.id ?? null
    return activeGroupId === ALL_GROUPS ? null : activeGroupId
  }, [isOperator, activeGroupId, user])

  const activeGroupName = useMemo(() => {
    if (!isOperator) return user?.dealer_group?.name ?? "No group"
    if (activeGroupId === ALL_GROUPS) return "All Groups"
    return groups.find((g) => g.id === activeGroupId)?.name ?? `Group ${activeGroupId}`
  }, [isOperator, activeGroupId, groups, user])

  const websiteDomain = useMemo((): string | null | undefined => {
    if (!isOperator) return user?.dealer_group?.website_domain ?? null
    if (activeGroupId === ALL_GROUPS) return null
    if (groupsLoaded) return groups.find((g) => g.id === activeGroupId)?.website_domain ?? null
    // The list is still loading; an operator's own group is known from login.
    if (user?.dealer_group?.id === activeGroupId) return user.dealer_group.website_domain
    return undefined
  }, [isOperator, activeGroupId, groups, groupsLoaded, user])

  const value = useMemo<DealerGroupContextValue>(
    () => ({
      activeGroupId,
      dealerGroupId,
      activeGroupName,
      websiteDomain,
      isOperator,
      groups,
      setActiveGroup,
    }),
    [activeGroupId, dealerGroupId, activeGroupName, websiteDomain, isOperator, groups, setActiveGroup],
  )

  return <DealerGroupContext.Provider value={value}>{children}</DealerGroupContext.Provider>
}

export const useDealerGroup = (): DealerGroupContextValue => {
  const ctx = useContext(DealerGroupContext)
  if (!ctx) {
    throw new Error("useDealerGroup must be used within DealerGroupProvider")
  }
  return ctx
}
